import { useMemo, useState } from 'react'
import {
  Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table,
} from 'react-bootstrap'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatDateTimeEsMX } from '../../utils/apiDateFormat'

const getClientName = (client) => {
  if (client?.razon_social) return client.razon_social
  if (client?.business_name) return client.business_name
  const parts = [client?.nombre, client?.apellido_paterno].filter(Boolean)
  return parts.length ? parts.join(' ') : (client?.rfc || `#${client?.id}`)
}

const estadoBadge = (estado) => {
  const map = {
    pendiente: 'secondary',
    solicitando: 'info',
    esperando_sat: 'warning',
    descargando: 'info',
    procesando: 'primary',
    correcto: 'success',
    error: 'danger',
  }
  return <Badge bg={map[estado] || 'secondary'}>{estado}</Badge>
}

const estatusLogBadge = (estatus) => {
  const map = {
    correcto: 'success',
    error: 'danger',
    procesado_previamente: 'warning',
    sin_xml: 'secondary',
  }
  return <Badge bg={map[estatus] || 'secondary'}>{estatus}</Badge>
}

/**
 * Descarga SAT manual + log (dentro de Procesamiento XML).
 * Solo admin / contador (el API también lo restringe).
 */
const DescargaSATPanel = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const role = user?.role
  const allowed = user?.is_superuser || ['admin', 'superadmin', 'contador'].includes(role)

  const { clientId: filterCliente, setClientId: setFilterCliente, selectDisabled } =
    useSyncedLocalClientId()

  // El unico tope es hoy: el SAT no tiene CFDI que todavia no existen. No hay limite de año
  // -- el que habia era invencion nuestra, no del SAT. Se aplica con el `max` del input, que
  // el navegador respeta antes de enviar el formulario; el backend lo valida igual para
  // cualquier otro camino, y su mensaje llega al catch del submit.
  const hoy = new Date().toISOString().slice(0, 10)

  const [cliente, setCliente] = useState('')
  const [fechaIni, setFechaIni] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { data: clients = [] } = useQuery(
    'clients-descarga-sat',
    async () => {
      const { data } = await api.get('/clients/clients/', { params: { page_size: 2000 } })
      return data.results || data || []
    },
    { enabled: !!user && allowed }
  )

  const solicitudesParams = useMemo(() => {
    const p = { page_size: 20 }
    if (filterCliente) p.cliente_id = filterCliente
    return p
  }, [filterCliente])

  const { data: solicitudesData, isLoading: loadingSol } = useQuery(
    ['descargas-sat', solicitudesParams],
    async () => {
      const { data } = await api.get('/accounting/descargas-sat/', { params: solicitudesParams })
      return data
    },
    { enabled: !!user && allowed, refetchInterval: 15000 }
  )

  const { data: logsData, isLoading: loadingLogs } = useQuery(
    ['descargas-sat-logs', solicitudesParams],
    async () => {
      const { data } = await api.get('/accounting/descargas-sat-logs/', {
        params: { ...solicitudesParams, page_size: 50 },
      })
      return data
    },
    { enabled: !!user && allowed, refetchInterval: 15000 }
  )

  if (!allowed) return null

  const solicitudes = solicitudesData?.results || solicitudesData || []
  const logs = logsData?.results || logsData || []

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!cliente || !fechaIni || !fechaFin) {
      toast.error('Seleccione cliente y rango de fechas')
      return
    }
    if (fechaFin < fechaIni) {
      toast.error('La fecha final no puede ser menor a la inicial')
      return
    }
    setSubmitting(true)
    try {
      await api.post('/accounting/descargas-sat/manual/', {
        cliente: Number(cliente),
        fecha_ini: fechaIni,
        fecha_fin: fechaFin,
      })
      toast.success('Descarga SAT encolada. El procesamiento sigue el mismo flujo que el cron.')
      queryClient.invalidateQueries('descargas-sat')
      queryClient.invalidateQueries('descargas-sat-logs')
    } catch (err) {
      const detail = err.response?.data?.detail
      const msg = Array.isArray(detail) ? detail.join(' ') : (detail || 'No se pudo encolar la descarga')
      toast.error(String(msg))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-4">
      <Card className="mb-4">
        <Card.Header>
          <strong>Descarga Masiva SAT</strong>
          <div className="text-muted small">
            Cron automático: solo el día de ejecución (hoy→hoy). Manual: cualquier rango pasado, sin límite de año.
            Subir FIEL no encola histórico automáticamente.
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    value={cliente}
                    onChange={(e) => setCliente(e.target.value)}
                    required
                  >
                    <option value="">Seleccionar…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {getClientName(c)} ({c.rfc})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha inicial *</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaIni}
                    max={hoy}
                    onChange={(e) => setFechaIni(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fecha final *</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaFin}
                    max={hoy}
                    onChange={(e) => setFechaFin(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Button type="submit" variant="primary" className="w-100" disabled={submitting}>
                  {submitting ? <Spinner size="sm" /> : 'Encolar'}
                </Button>
              </Col>
            </Row>
          </Form>
          <Alert variant="light" className="mt-3 mb-0 border">
            Si el rango solapa fechas ya en cola (p. ej. el día del cron), la API rechaza la petición
            listando <strong>todas</strong> las fechas en conflicto. Si el rango trae más CFDI de los que
            el SAT entrega en una sola solicitud, responde <strong>5003</strong> y hay que partirlo en tramos.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Solicitudes / cola</strong>
          <Form.Select
            size="sm"
            style={{ maxWidth: 280 }}
            value={filterCliente || ''}
            onChange={(e) => setFilterCliente(e.target.value || '')}
            disabled={selectDisabled}
          >
            <option value="">Todos los clientes</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{getClientName(c)}</option>
            ))}
          </Form.Select>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingSol ? (
            <div className="p-3"><Spinner size="sm" /> Cargando…</div>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Rango</th>
                  <th>Origen</th>
                  <th>Estado</th>
                  <th>Bajados</th>
                  <th>Procesados</th>
                  <th>Excluidos</th>
                  <th>Petición</th>
                  <th>Proceso</th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-muted py-3">Sin solicitudes</td></tr>
                ) : solicitudes.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id}</td>
                    <td>{s.cliente_nombre}<div className="small text-muted">{s.cliente_rfc}</div></td>
                    <td>{s.fecha_ini} → {s.fecha_fin}</td>
                    <td>{s.origen_display || s.origen}</td>
                    <td>{estadoBadge(s.estado)}</td>
                    <td>{s.uuids_bajados}</td>
                    <td>{s.uuids_procesados}</td>
                    <td>{s.uuids_excluidos}</td>
                    <td>{formatDateTimeEsMX(s.fecha_peticion)}</td>
                    <td>{s.fecha_proceso ? formatDateTimeEsMX(s.fecha_proceso) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><strong>Log de procesamiento (UUIDs)</strong></Card.Header>
        <Card.Body className="p-0">
          {loadingLogs ? (
            <div className="p-3"><Spinner size="sm" /> Cargando…</div>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Solicitud</th>
                  <th>Cliente</th>
                  <th>UUID</th>
                  <th>Estatus</th>
                  <th>Mensaje</th>
                  <th>Petición</th>
                  <th>Proceso</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-muted py-3">Sin logs</td></tr>
                ) : logs.map((l) => (
                  <tr key={l.id}>
                    <td>{l.solicitud}</td>
                    <td>{l.cliente_nombre}</td>
                    <td className="font-monospace small">{l.uuid || '—'}</td>
                    <td>{estatusLogBadge(l.estatus)}</td>
                    <td className="small">{l.mensaje}</td>
                    <td>{formatDateTimeEsMX(l.fecha_peticion)}</td>
                    <td>{formatDateTimeEsMX(l.fecha_proceso)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default DescargaSATPanel
